/**
 * platform-role.ts
 * Shared Zod contracts for platform-tier role management (KI-29 / KI-30 / RM-CAP-6).
 *
 * Covers:
 *   - PlatformRole: the DTO returned by GET /platform/companies/:id/roles
 *   - RoleListResponse: array wrapper for role list
 *   - CreateRoleBody: POST /platform/companies/:id/roles request body
 *   - UpdateRoleBody: PUT /platform/companies/:id/roles/:roleId request body
 *   - RoleResponse: single-role response (201 created / 200 updated)
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 *
 * Capability key shape: '<ámbito>.<recurso>.<acción>' (SDD-roles.md §1, with
 * the acceso.* 2-segment exception — see the regex below). The allow-list
 * check (isCatalogCapability) is performed server-side; the regex here is a
 * first-pass shape guard only.
 *
 * Spec: KI-31 / RM-CAP-6 / KI-29 / KI-30 / SDD-roles.md §1 / §7 Fase 5
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Capability key shape guard — SDD-roles.md §1 (dot-vocabulary, replaces the
// legacy AZ-6.4 `<namespace>:<action>` shape as of migration 0154).
// Server-side: also checked against CAPABILITY_KEY_SET (allow-list).
// ---------------------------------------------------------------------------

const capabilityKeySchema = z
  .string()
  .regex(
    // Reverted to hyphen-only (2026-10-05, reorganizar-cuentas-pagar-bajo-sumi,
    // design D9, spec AZ-19 regex-reversion amendment): the underscore
    // allowance added 2026-09-17 existed ONLY because `cuentas_pagar` was a
    // real Postgres schema/moduleKey (migration 0197) that could not be
    // renamed without touching a live schema. That schema no longer exists
    // (migration 0210 folded it into `sumi`) and its 7 capabilities were
    // renamed to the `sumi.*` namespace (migration 0211) — no catalog key
    // uses an underscore any more, so the widened shape guard is reverted.
    /^[a-z][a-z-]*\.[a-z][a-z-]*(\.[a-z][a-z-]*)?$/,
    'capability key must match <ámbito>.<recurso>.<acción> pattern',
  );

// ---------------------------------------------------------------------------
// PlatformRole — returned by list and single-role responses (RM-CAP-2)
// ---------------------------------------------------------------------------

export const PlatformRoleSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The permissions array stored in identity.role.permissions. */
  capabilities: z.array(z.string()),
  /**
   * True when this is a built-in role (admin, operator, jefe_compras, etc.).
   * Built-in roles cannot be edited or deleted via platform API (RM-CAP-4).
   */
  isBuiltIn: z.boolean(),
});

export type PlatformRole = z.infer<typeof PlatformRoleSchema>;

// ---------------------------------------------------------------------------
// RoleListResponse — GET /platform/companies/:id/roles (KI-29.1 / RM-CAP-2)
// ---------------------------------------------------------------------------

export const RoleListResponseSchema = z.object({
  roles: z.array(PlatformRoleSchema),
});

export type RoleListResponse = z.infer<typeof RoleListResponseSchema>;

// ---------------------------------------------------------------------------
// CreateRoleBody — POST /platform/companies/:id/roles (KI-29.3 / RM-CAP-3)
// ---------------------------------------------------------------------------

export const CreateRoleBodySchema = z.object({
  name: z
    .string()
    .min(1, 'name is required')
    .max(100, 'name must be at most 100 characters')
    .trim(),
  // KI-30.3: empty array [] is valid — a role with no granted capabilities.
  // Individual strings are shape-checked; server-side catalog allow-list is the real gate.
  capabilities: z.array(capabilityKeySchema),
});

export type CreateRoleBody = z.infer<typeof CreateRoleBodySchema>;

// ---------------------------------------------------------------------------
// UpdateRoleBody — PATCH /platform/companies/:id/roles/:roleId (KI-29.4 / RM-CAP-4)
// ---------------------------------------------------------------------------

export const UpdateRoleBodySchema = z
  .object({
    name: z
      .string()
      .min(1, 'name is required')
      .max(100, 'name must be at most 100 characters')
      .trim()
      .optional(),
    capabilities: z.array(capabilityKeySchema).optional(),
  })
  .refine((data) => data.name !== undefined || data.capabilities !== undefined, {
    message: 'At least one of name or capabilities must be provided.',
  });

export type UpdateRoleBody = z.infer<typeof UpdateRoleBodySchema>;

// ---------------------------------------------------------------------------
// RoleResponse — single-role response body (201 / 200) (RM-CAP-3 / RM-CAP-4)
// ---------------------------------------------------------------------------

export const RoleResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  capabilities: z.array(z.string()),
  isBuiltIn: z.boolean(),
});

export type RoleResponse = z.infer<typeof RoleResponseSchema>;
